import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from './surfacePrimitives.js';

export interface ScreenRefreshButtonProps {
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly testId?: string;
  readonly className?: string;
  /**
   * Prefer the shared key. Override only for permanently specialized ops labels
   * (e.g. “Refresh diagnostics”) — never for ordinary list/hub refresh.
   */
  readonly labelKey?: string;
  readonly refreshingLabelKey?: string;
}

/**
 * Canonical in-content “Obnovit” control — same label, icon, and secondary intent
 * on every screen. Must not mount in `EnterprisePageHeader` (sacred global chrome).
 */
export const ScreenRefreshButton = memo<ScreenRefreshButtonProps>(
  ({
    onClick,
    disabled = false,
    loading = false,
    testId = 'screen-refresh-btn',
    className,
    labelKey = 'shared.listToolbar.refresh',
    refreshingLabelKey = 'shared.listToolbar.refreshing',
  }) => {
    const { t } = useTranslation('admin');
    const busy = disabled || loading;
    const label = loading ? t(refreshingLabelKey) : t(labelKey);

    return (
      <Button
        type="button"
        intent="secondary"
        size="sm"
        onClick={onClick}
        disabled={busy}
        loading={loading}
        title={t(labelKey)}
        aria-label={t(labelKey)}
        data-testid={testId}
        className={className}
      >
        <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
        {label}
      </Button>
    );
  },
);

ScreenRefreshButton.displayName = 'ScreenRefreshButton';
